import { batch, useSignal } from "@preact/signals";
import { rpc } from "../rpc";
import { HISTORY_PAGE_SIZE, type HistoryItem } from "../types";

type UseHistoryOptions = {
  orgId: string | null;
  initialHistory: HistoryItem[];
  initialHasMore: boolean;
};

/**
 * Hook for managing paginated submission history.
 * Handles loading state, error state, and pagination.
 */
export function useHistory({
  orgId,
  initialHistory,
  initialHasMore,
}: UseHistoryOptions) {
  const history = useSignal<HistoryItem[]>(initialHistory);
  const hasMore = useSignal(initialHasMore);
  const loadingMore = useSignal(false);
  const loadMoreError = useSignal<string | null>(null);

  async function loadMore() {
    if (loadingMore.value || !orgId) return;
    loadingMore.value = true;

    try {
      const result = await rpc.submissions.myHistory({
        orgId,
        limit: HISTORY_PAGE_SIZE,
        offset: history.value.length,
      });
      // Coerce date strings to Date objects (RPC JSON doesn't preserve Date types)
      const items = result.submissions.map((s: HistoryItem) => ({
        ...s,
        periodStart: new Date(s.periodStart),
        submittedAt: new Date(s.submittedAt),
      }));
      batch(() => {
        history.value = [...history.value, ...items];
        hasMore.value = result.hasMore;
        loadingMore.value = false;
        loadMoreError.value = null;
      });
    } catch (err) {
      batch(() => {
        loadMoreError.value =
          err instanceof Error ? err.message : "Failed to load history";
        loadingMore.value = false;
      });
    }
  }

  return {
    history,
    hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
  };
}
